import React, { useState, useEffect } from 'react';
import axios from 'axios';
import logger from '../utils/logger';


const DailyPlanner = () => {
  const [rolesAndGoals, setRolesAndGoals] = useState([]);
  const [tasksAndObjectives, setTasksAndObjectives] = useState({});
  const [newTask, setNewTask] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchRolesAndGoals();
    fetchTasksAndObjectives();
  }, [selectedDate]);

  const fetchRolesAndGoals = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('https://backend-rough-bird-115.fly.dev/api/roles-and-goals', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRolesAndGoals(response.data);
    } catch (error) {
      logger.error('Error fetching roles and goals:', error.response?.data || error.message);
      setError('Failed to fetch roles and goals');
    }
  };

  const fetchTasksAndObjectives = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`https://backend-rough-bird-115.fly.dev/api/daily-planner`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { date: selectedDate }
      });
      setTasksAndObjectives(response.data);
    } catch (error) {
      logger.error('Error fetching tasks and objectives:', error.response?.data || error.message);
      setError('Failed to fetch tasks and objectives');
    }
  };

  const handleAddTask = async (roleGoalId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('https://backend-rough-bird-115.fly.dev/api/daily-planner', 
        { 
          roleGoalId,
          task: newTask,
          date: selectedDate,
          completed: false
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTasksAndObjectives({
        ...tasksAndObjectives,
        [roleGoalId]: {
          ...tasksAndObjectives[roleGoalId],
          tasks: [...(tasksAndObjectives[roleGoalId]?.tasks || []), response.data]
        }
      });
      setNewTask('');
      setMessage('Task added successfully');
    } catch (error) {
      logger.error('Error adding task:', error.response?.data || error.message);
      setError(`Failed to add task: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleToggleTask = async (taskId, completed) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`https://backend-rough-bird-115.fly.dev/api/daily-planner/${taskId}`, 
        { completed },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTasksAndObjectives(prevState => {
        const updatedState = { ...prevState };
        for (const roleGoalId in updatedState) {
          updatedState[roleGoalId].tasks = updatedState[roleGoalId].tasks.map(task => 
            task._id === taskId ? response.data : task
          );
        }
        return updatedState;
      });
    } catch (error) {
      logger.error('Error updating task:', error.response?.data || error.message);
      setError(`Failed to update task: ${error.response?.data?.message || error.message}`);
    }
  };

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-4">Daily Planner</h1>
      {error && <p className="text-red-500 mb-4">{error}</p>}
      {message && <p className="text-green-500 mb-4">{message}</p>}

      <div className="mb-4">
        <label htmlFor="date" className="block text-gray-700 text-sm font-bold mb-2">Select Date:</label>
        <input
          type="date"
          id="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
        />
      </div>

      {rolesAndGoals.map((roleGoal) => (
        <div key={roleGoal._id} className="mb-6">
          <h2 className="text-xl font-bold mb-2">{roleGoal.role}: {roleGoal.goal}</h2>
          
          <div className="mb-4">
            <h3 className="text-lg font-semibold mb-2">2-Week Objectives</h3>
            <ul className="list-disc list-inside mb-2">
              {tasksAndObjectives[roleGoal._id]?.objectives.map((objective) => (
                <li key={objective._id}>{objective.objective}</li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-2">Tasks</h3>
            <ul className="space-y-2">
              {tasksAndObjectives[roleGoal._id]?.tasks.map((task) => (
                <li key={task._id} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={task.completed}
                    onChange={() => handleToggleTask(task._id, !task.completed)}
                    className="mr-2"
                  />
                  <span className={task.completed ? 'line-through' : ''}>{task.task}</span>
                </li>
              ))}
            </ul>
            <div className="mt-2 flex">
              <input
                type="text"
                value={newTask}
                onChange={(e) => setNewTask(e.target.value)}
                placeholder="New task"
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline mr-2"
              />
              <button
                onClick={() => handleAddTask(roleGoal._id)}
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
              >
                Add Task
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default DailyPlanner;